import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getOfferings, hasActiveSubscription } from '../services/revenuecat';
import { useAuth } from '../contexts/AuthContext';
import { DisplayPackage } from '../types/revenuecat';
import { Deal, dealFromMap } from '../types/deal';
import { getTrialDisplayText } from '../utils/duration';
import { validateCouponCode, fetchStripePrices, fetchEarlyBirdOffer, EarlyBirdOffer, StripePriceWithDiscount } from '../services/stripePricing';
import { isEarlyBirdOffer, clearEarlyBirdOffer, EARLY_BIRD_MIN_FREE_LEAD_MS } from '../utils/earlyBird';
import { formatLongDate } from '../utils/formatDate';
import { getTrialDuration } from '../services/firebase';
import { getOfferingsFromCache, saveOfferingsToCache } from '../utils/offeringsCache';
import { getCurrencySymbol } from '../utils/currency';
import { trackCouponSubmitted, trackPlanSelected } from '../services/analytics';
import { getPendingDealDoc, trackBranchEvent } from '../services/branch';
import DealBanner from './DealBanner';
import './Offers.css';
import LoadingRive from './LoadingRive';
import { t } from '../i18n';

const logo = `${process.env.PUBLIC_URL}/logo.svg`;

const Offers: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userDocData, subscriber, logout } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [packages, setPackages] = useState<DisplayPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activeDeal, setActiveDeal] = useState<Deal | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponValidation, setCouponValidation] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<any>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // Track if user is changing plan
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [currentPriceId, setCurrentPriceId] = useState<string | null>(null);

  // Early bird: a partner-code runner taking the discounted annual price.
  // Not while changing plan: that flow updates an existing subscription.
  const [earlyBird] = useState(() => {
    if ((location.state as { isChangingPlan?: boolean } | null)?.isChangingPlan) {
      clearEarlyBirdOffer();
      return false;
    }
    return isEarlyBirdOffer();
  });
  const [earlyBirdFreeUntil, setEarlyBirdFreeUntil] = useState<string | null>(null);
  // Set when the server refuses the offer: retrying won't change the answer.
  const [earlyBirdRefused, setEarlyBirdRefused] = useState(false);
  // The normal price of the same plan, shown struck through.
  const [earlyBirdRegularTotal, setEarlyBirdRegularTotal] = useState<number | null>(null);

  useEffect(() => {
    checkSubscriptionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Check for error passed from navigation state (e.g., from Billing redirect)
  useEffect(() => {
    const state = location.state as { error?: string };
    if (state?.error) {
      setError(state.error);
      setLoading(false);
    }
  }, [location]);

  // Check for pending Branch deals after packages are loaded
  useEffect(() => {
    if (packages.length > 0 && currentUser) {
      applyPendingBranchDeal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages, currentUser, userDocData]);

  const applyPendingBranchDeal = async () => {
    console.log('[Branch Deal] Checking for deals...');

    // First check if user has an active deal in their profile (from auth context)
    if (userDocData?.settings?.active_deal) {
      console.log('[Branch Deal] Active deal found in user profile:', userDocData.settings.active_deal.name);
      const dealObj = dealFromMap(userDocData.settings.active_deal);
      setActiveDeal(dealObj);
      return;
    } else {
      console.log('[Branch Deal] No active deal in user profile');
    }

    // Check for pending deal in localStorage (if they haven't logged in yet)
    console.log('[Branch Deal] Checking localStorage for pending deal...');
    const pendingDeal = getPendingDealDoc();

    if (!pendingDeal) {
      console.log('[Branch Deal] No pending deal found in localStorage');
      console.log('[Branch Deal] To test: 1) Create a deal in Firestore at /deals/{dealId}, 2) Create a Branch link with deal_id parameter, 3) Click the link');
      return;
    }

    console.log('[Branch Deal] Applying pending deal:', pendingDeal.name);

    // Track that we're applying a Branch deal
    trackBranchEvent('DEAL_APPLIED', {
      dealId: pendingDeal.id,
      dealName: pendingDeal.name,
    });

    // Display deal banner
    setActiveDeal(pendingDeal);

    // Clear the pending deal after displaying (it's now saved to user profile if logged in)
    // clearPendingDeal(); // Keep it until user completes purchase or it's saved to profile
  };

  // Runs synchronously: the subscriber comes from AuthContext, which already
  // fetched it on sign-in. No network round trip before offers start loading.
  const checkSubscriptionStatus = () => {
    if (!currentUser?.uid) return;

    // Check if user is coming to change their plan
    const state = location.state as { isChangingPlan?: boolean; currentPriceId?: string };
    if (state?.isChangingPlan) {
      // User is changing plans, skip redirect and load offerings
      console.log('User is changing plan, loading offerings...');
      setIsChangingPlan(true);
      if (state.currentPriceId) {
        setCurrentPriceId(state.currentPriceId);
      }
      loadOfferings();
      return;
    }

    // Early bird runners are on an active promo (and a code deal) by design,
    // so skip both redirects below. The server refuses anyone already paying.
    if (earlyBird) {
      loadOfferings();
      return;
    }

    try {
      // Check if user already has an active subscription
      if (hasActiveSubscription(subscriber)) {
        // Redirect to subscription details page (works for all stores: stripe, app_store, play_store, promotional)
        navigate('/subscription');
        return;
      }

      // If the active deal is a code-redemption deal, send the user to the redemption page
      const dealMap = userDocData?.settings?.active_deal || getPendingDealDoc();
      var isRedemption = false;
      if (dealMap) {
        isRedemption = dealFromMap(dealMap).isRedemption ?? true;
        console.log(isRedemption);
      }
      if (isRedemption) {
        navigate('/redeem');
        return;
      }

      // No active subscription, load offerings
      loadOfferings();
    } catch (err) {
      console.error('Error checking subscription status:', err);
      // Continue to load offerings even if check fails
      loadOfferings();
    }
  };

  const loadOfferings = async () => {
    setError('');

    // Paint cached offers straight away, then revalidate behind them. A stale
    // price for a few hundred ms beats an empty spinner.
    // The early bird list is per-runner, so it never reads or fills the cache.
    const cachedPackages = earlyBird ? null : getOfferingsFromCache();
    const hasCache = !!cachedPackages && cachedPackages.length > 0;
    if (hasCache) {
      console.log('Serving offers from cache, revalidating in background');
      setPackages(cachedPackages!);
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      // Stripe prices are the source of truth; the RevenueCat offering only
      // adds metadata. Neither depends on the other, so fetch them together
      // rather than paying two cold starts back to back.
      const [priceResult, data, regularPrices] = await Promise.all([
        earlyBird ? fetchEarlyBirdOffer() : fetchStripePrices(),
        getOfferings(currentUser?.uid),
        // Early bird compares against the normal price; not worth failing over.
        earlyBird ? fetchStripePrices().catch(() => [] as StripePriceWithDiscount[]) : Promise.resolve([] as StripePriceWithDiscount[]),
      ]);

      let stripePrices: StripePriceWithDiscount[];
      let earlyBirdRegularAmount: number | null = null;
      if (earlyBird) {
        const offer = priceResult as EarlyBirdOffer;
        if (!offer.eligible) {
          setEarlyBirdRefused(true);
          setError(t(offer.reason === 'already_subscribed' ? 'offers.earlyBirdAlreadySubscribed' : 'offers.earlyBirdUnavailable'));
          return;
        }
        // Same rule as Billing: under 2h left is shown as pay today, so the
        // two pages never disagree about what's due.
        const freeLongEnough = !!offer.freeUntil &&
          new Date(offer.freeUntil).getTime() - Date.now() > EARLY_BIRD_MIN_FREE_LEAD_MS;
        setEarlyBirdFreeUntil(freeLongEnough ? offer.freeUntil : null);
        stripePrices = offer.prices;
        const offerPrice = offer.prices[0];
        // The "was" price: set on the offer price (e.g. CAD, which has no
        // normal plan to compare with), else the same plan's normal price.
        const compareAt = Number(offerPrice?.metadata?.compare_at_amount) || null;
        const regular = offerPrice && regularPrices.find(p =>
          p.productId === offerPrice.productId &&
          p.currency === offerPrice.currency &&
          p.interval === offerPrice.interval
        );
        const regularAmount = compareAt ?? regular?.amount ?? null;
        earlyBirdRegularAmount = offerPrice && regularAmount && regularAmount > offerPrice.amount ? regularAmount : null;
        setEarlyBirdRegularTotal(earlyBirdRegularAmount ? earlyBirdRegularAmount / 100 : null);
      } else {
        stripePrices = priceResult as StripePriceWithDiscount[];
      }
      console.log('Stripe prices:', stripePrices);

      if (!stripePrices || stripePrices.length === 0) {
        if (!hasCache) setError(t('offers.noPlansAvailable'));
        return;
      }

      // Create a map of RevenueCat products by store_identifier for metadata lookup
      const revenueCatMap = new Map<string, any>();

      if (data && data.items && data.items.length > 0) {
        console.log('Processing RevenueCat offerings for metadata:', data.items.length);

        for (const offering of data.items) {
          if (offering.packages?.items) {
            for (const pkg of offering.packages.items) {
              // Get the first eligible product (prioritize "all" eligibility)
              const allEligible = pkg.products.items.find((p: any) => p.eligibility_criteria === 'all');
              const productItem = allEligible || pkg.products.items[0];

              // Store RevenueCat metadata by store_identifier (matches Stripe productId)
              revenueCatMap.set(productItem.product.store_identifier, {
                packageId: pkg.id,
                packageDisplayName: pkg.display_name,
                packageLookupKey: pkg.lookup_key,
                packagePosition: pkg.position,
                productId: productItem.product.id,
                productDisplayName: productItem.product.display_name,
                duration: productItem.product.subscription.duration,
                trialDuration: productItem.product.subscription.trial_duration,
              });
            }
          }
        }
        console.log('RevenueCat metadata map created:', revenueCatMap.size, 'products');
      }

      // Step 3: Transform Stripe prices into display packages with RevenueCat metadata
      const allPackages: DisplayPackage[] = stripePrices.map(stripePrice => {
        // Get RevenueCat metadata if available
        const revenueCatData = revenueCatMap.get(stripePrice.productId);

        // Get months from Stripe metadata (default to 1 if not set)
        const months = stripePrice.metadata?.months ? parseInt(stripePrice.metadata.months, 10) : 1;

        // Get trial duration from Remote Config (can be changed without redeploying)
        const trialDuration = getTrialDuration();

        // Calculate price per month
        const totalAmount = stripePrice.amount / 100; // Total price
        const pricePerMonth = totalAmount / months; // Price divided by months

        // Get currency symbol
        const currencySymbol = getCurrencySymbol(stripePrice.currency);

        // Format display strings based on number of months
        // Use Stripe product name as the base (e.g., "Monthly", "Yearly")
        const baseName = stripePrice.productName;

        // Always show price per month
        const priceString = `${currencySymbol}${pricePerMonth.toFixed(2)} / month`;

        // Find the monthly price for comparison
        let monthlyPrice = null;
        if (months > 1) {
          // Try to find a Stripe price with months = 1 and same currency
          const monthly = stripePrices.find(p => {
            const m = p.metadata?.months ? parseInt(p.metadata.months, 10) : 1;
            return m === 1 && p.currency === stripePrice.currency;
          });
          if (monthly) {
            monthlyPrice = monthly.amount / 100;
          }
        }

        // Calculate percent off
        let percentOff: number | undefined = undefined;
        if (earlyBirdRegularAmount) {
          // Early bird: off the normal price of the same plan.
          percentOff = Math.round(100 - (stripePrice.amount / earlyBirdRegularAmount) * 100);
        } else if (monthlyPrice && months > 1) {
          const totalMonthly = monthlyPrice * months;
          percentOff = Math.round(100 - (totalAmount / totalMonthly) * 100);
        }

        return {
          identifier: revenueCatData?.packageId || `stripe_${stripePrice.id}`,
          display_name: baseName,
          lookup_key: revenueCatData?.packageLookupKey || stripePrice.id,
          position: revenueCatData?.packagePosition || 0,
          product: {
            id: revenueCatData?.productId || stripePrice.productId,
            price_id: stripePrice.id,
            title: baseName,
            totalPrice: months > 1 ? totalAmount : null, // Store total price for multi-month plans
            store_identifier: stripePrice.productId,
            duration: revenueCatData?.duration || null,
            // Early bird: free until the code ends, not the standard trial.
            trial_duration: earlyBird ? null : trialDuration,
            price: stripePrice.amount,
            price_string: priceString,
            currency: stripePrice.currency,
            interval: stripePrice.interval,
          },
          percentOff: percentOff === undefined ? undefined : percentOff,
          // Store months for sorting
          _sortMonths: months,
        };
      });

      // Sort packages by months (smallest first)
      allPackages.sort((a, b) => {
        const monthsA = (a as any)._sortMonths || 1;
        const monthsB = (b as any)._sortMonths || 1;
        return monthsA - monthsB;
      });

      // Save to cache before setting state
      if (!earlyBird) saveOfferingsToCache(allPackages);

      setPackages(allPackages);
      if (earlyBird && allPackages.length === 1) setSelectedPlan(allPackages[0].identifier);
      console.log('Display packages created from Stripe products (sorted by months):', allPackages);

      // Don't auto-select - let user choose
      // Button will be disabled until user selects a package
    } catch (err) {
      console.error('Error loading offerings:', err);
      // Keep showing the cached offers if a background revalidation fails.
      if (!hasCache) setError(t('offers.failedToLoadPlans'));
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponValidation({ success: false, message: t('offers.enterCouponCodeError') });
      return;
    }

    if (!selectedPlan) {
      setCouponValidation({ success: false, message: t('offers.selectPlanFirst') });
      return;
    }

    const selectedPackage = packages.find(pkg => pkg.identifier === selectedPlan);
    if (!selectedPackage) return;

    // Find the Stripe price ID for this package
    let priceId = null;
    if (selectedPackage && selectedPackage.product) {
      priceId = selectedPackage.product.price_id;
    }
    if (!priceId) {
      setCouponValidation({ success: false, message: t('offers.unableToValidateCoupon') });
      return;
    }

    setValidatingCoupon(true);
    setCouponValidation(null);

    try {
      const result = await validateCouponCode(
        couponCode.toUpperCase(),
        priceId
      );

      if (result.valid && result.coupon && result.pricing) {
        const currency = selectedPackage.product.currency || 'gbp';
        const currencySymbol = getCurrencySymbol(currency);

        setAppliedDiscount(result);
        setCouponValidation({
          success: true,
          message: t('offers.couponApplied', { discount: result.coupon.percentOff ? `${result.coupon.percentOff}% off` : `${currencySymbol}${(result.coupon.amountOff! / 100).toFixed(2)} off` })
        });

        trackCouponSubmitted(
          couponCode.toUpperCase(),
          currentUser?.email || undefined,
          currentUser?.uid,
          true
        );
      } else {
        setAppliedDiscount(null);
        let errorMessage = t('offers.invalidCouponCode');

        if (result.error) {
          const errorLower = result.error.toLowerCase();
          if (errorLower.includes('expired')) {
            errorMessage = t('offers.couponExpired');
          } else if (errorLower.includes('limit') || errorLower.includes('redemption')) {
            errorMessage = t('offers.couponLimitReached');
          } else if (errorLower.includes('not found') || errorLower.includes('does not exist')) {
            errorMessage = t('offers.invalidCouponCode');
          } else if (errorLower.includes('not valid')) {
            errorMessage = t('offers.couponNotValidForPlan');
          }
        }

        setCouponValidation({
          success: false,
          message: errorMessage
        });

        trackCouponSubmitted(
          couponCode.toUpperCase(),
          currentUser?.email || undefined,
          currentUser?.uid,
          false
        );
      }
    } catch (err: any) {
      setAppliedDiscount(null);
      setCouponValidation({
        success: false,
        message: t('offers.unableToValidate')
      });

      trackCouponSubmitted(
        couponCode.toUpperCase(),
        currentUser?.email || undefined,
        currentUser?.uid,
        false
      );
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleContinue = () => {
    if (selectedPlan) {
      // Find the complete package object
      const selectedPackage = packages.find(pkg => pkg.identifier === selectedPlan);
      if (selectedPackage) {
        // Track plan selection
        trackPlanSelected(
          selectedPackage.product.title || selectedPackage.identifier,
          selectedPackage.product.price || 0, // price is already in cents
          selectedPackage.product.currency || 'gbp',
          selectedPackage.product.interval || 'unknown',
          currentUser?.email || undefined,
          currentUser?.uid
        );

        // Get state from navigation (includes promotional subscription info if changing plan)
        const state = location.state as {
          isChangingPlan?: boolean;
          currentSubscriptionExpiry?: string;
          currentSubscriptionStore?: string;
        };

        console.log('Offers - handleContinue with location state:', state);

        // Pass complete package and coupon info to billing page
        navigate('/billing', {
          state: {
            selectedPackage,
            couponCode: appliedDiscount ? couponCode : undefined,
            discountInfo: appliedDiscount,
            isChangingPlan: isChangingPlan,
            currentSubscriptionExpiry: state?.currentSubscriptionExpiry,
            currentSubscriptionStore: state?.currentSubscriptionStore,
            isEarlyBird: earlyBird,
            earlyBirdFreeUntil,
          }
        });
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  if (loading) {
    return (
      <div className="offers-container">
        <div className="offers-background" />
        <div className="offers-content" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '360px'}}>
          <LoadingRive animation="LogoFillingLoader" label={t('offers.loadingOffers')} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="offers-container">
        <div className="offers-background" />
        <div className="offers-content">
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#FF9999', fontSize: '18px', marginBottom: '16px' }}>{error}</p>
            {!earlyBirdRefused && (
              <button
                onClick={loadOfferings}
                style={{
                  background: '#E4FF78',
                  color: '#1B2B48',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontFamily: 'Figtree, sans-serif'
                }}
              >
                {t('common.retry')}
              </button>
            )}
            {earlyBird && (
              <>
                <br />
                <button
                  className="offers-error-secondary"
                  onClick={() => {
                    // Their account page; it sends non-subscribers on to the plans.
                    clearEarlyBirdOffer();
                    window.location.assign('/subscription');
                  }}
                >
                  {t('offers.earlyBirdGoToAccount')}
                </button>
              </>
            )}
            <br />
            <button
              onClick={handleLogout}
              style={{
                background: 'transparent',
                color: '#FAFDFB',
                border: '2px solid rgba(250, 253, 251, 0.16)',
                borderRadius: '16px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '12px',
                fontFamily: 'Figtree, sans-serif'
              }}
            >
              {t('common.goBack')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="offers-container">
      <div className="offers-background" />

            <div className="offers-content">
        <div className="offers-header">
          {activeDeal ? (
            <DealBanner deal={activeDeal} variant={earlyBird ? 'hero' : 'default'} />
          ) : (
            <a href="https://urunn.com" style={{ cursor: 'pointer' }}>
              <img src={logo} alt="Urunn" className="urunn-logo" />
            </a>
          )}
          <h1 className={`offers-title${earlyBird ? ' offers-title--sentence' : ''}`}>
            {!earlyBird
              ? t('offers.title')
              : !packages[0]?.percentOff
                ? t('offers.earlyBirdTitleGeneric')
                : t(earlyBirdFreeUntil ? 'offers.earlyBirdTitle' : 'offers.earlyBirdTitleEnded', { percent: packages[0].percentOff })}
          </h1>
          {earlyBird && (
            <p className="offers-subtitle">
              {earlyBirdFreeUntil
                ? t('offers.earlyBirdFreeUntil', { date: formatLongDate(earlyBirdFreeUntil) })
                : t('offers.earlyBirdChargedToday')}
            </p>
          )}
          <button
            className="logout-button"
            onClick={handleLogout}
            aria-label={t('common.logout')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10.6667 11.3333L14 8L10.6667 4.66667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('common.logout')}
          </button>
        </div>

        <div className="plans-section">
          <div className={`plans-grid ${packages.length === 1 ? 'single' : ''}`}>
            {packages.map((pkg) => {
              // Get currency symbol for this package
              const currency = pkg.product.currency || 'gbp';
              const currencySymbol = getCurrencySymbol(currency);

              // Check if this is the current plan
              const isCurrentPlan = currentPriceId && pkg.product.price_id === currentPriceId;

              return (
                <div
                  key={pkg.identifier}
                  className={`plan-card ${selectedPlan === pkg.identifier ? 'selected' : ''} ${isCurrentPlan ? 'current-plan' : ''}`}
                  onClick={() => !isCurrentPlan && setSelectedPlan(pkg.identifier)}
                  style={{ opacity: isCurrentPlan ? 0.6 : 1, cursor: isCurrentPlan ? 'not-allowed' : 'pointer' }}
                >
                  {isCurrentPlan && (
                    <div className="current-plan-badge">
                      {t('offers.currentPlan')}
                    </div>
                  )}
                  {/* Discount badge for multi-month plans */}
                  {pkg.percentOff && pkg.percentOff > 0 && (
                    <div className="discount-badge">
                      {t('offers.percentOff', { percent: pkg.percentOff })}
                    </div>
                  )}
                  <div className="plan-content">
                    <div className="plan-header">
                      <img src={logo} alt="Urunn" className="urunn-logo-small" />
                      <div className="plan-name-group">
                        <h2 className="plan-name">
                          {pkg.product.title}
                        </h2>
                        {pkg.product.totalPrice && (
                          <span className="plan-total">
                            {currencySymbol}{pkg.product.totalPrice.toFixed(2)}
                          </span>
                        )}
                        {earlyBird && earlyBirdRegularTotal && (
                          <span className="price-amount-original">
                            {currencySymbol}{earlyBirdRegularTotal.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="plan-divider" />

                    <div className="plan-price">
                      {appliedDiscount && selectedPlan === pkg.identifier ? (
                        <>
                          <span className="price-amount-original">
                            {currencySymbol}{(appliedDiscount.pricing.originalAmount / 100).toFixed(2)}
                          </span>
                          <span className="price-amount">
                            {currencySymbol}{(appliedDiscount.pricing.discountedAmount / 100).toFixed(2)}
                          </span>
                          <span className="savings-badge">
                            {t('offers.save', { amount: `${currencySymbol}${((appliedDiscount.pricing.originalAmount - appliedDiscount.pricing.discountedAmount) / 100).toFixed(2)}` })}
                          </span>
                        </>
                      ) : (
                        <span className="price-amount">
                          {pkg.product.price_string}
                        </span>
                      )}
                    </div>
                  </div>

                  {pkg.product.trial_duration && (
                    <div className="plan-badge">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.3333 7.38668V8.00001C13.3325 9.43763 12.8637 10.8365 12 11.9879C11.1363 13.1393 9.9431 13.9817 8.58735 14.3893C7.23159 14.797 5.78318 14.7479 4.4561 14.2499C3.12902 13.7519 2 12.8304 1.23193 11.6247C0.463853 10.4189 0.096973 8.99205 0.184466 7.55755C0.271959 6.12305 0.809403 4.75763 1.71617 3.65537C2.62294 2.55311 3.84827 1.77437 5.21923 1.43904C6.59019 1.10372 8.03297 1.22902 9.33333 1.80001" stroke="#FAFDFB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M13.3333 2.66667L8 8.00667L6 6.00667" stroke="#FAFDFB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{getTrialDisplayText(pkg.product.trial_duration)}</span>
                    </div>
                  )}
                </div>
              );
            })}

            {packages.length === 0 && !loading && (
              <p style={{ color: '#FAFDFB', gridColumn: '1 / -1', textAlign: 'center' }}>
                {t('offers.noPlansMessage')}
              </p>
            )}
          </div>

          <div className="coupon-section" style={{ display: 'none' }}>
            <div className="coupon-input-wrapper">
              <input
                type="text"
                className="coupon-input"
                placeholder={t('offers.enterCouponCode')}
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value.toUpperCase());
                  setCouponValidation(null);
                }}
                disabled={validatingCoupon || !selectedPlan}
              />
              <button
                className="btn-apply-coupon"
                onClick={handleApplyCoupon}
                disabled={validatingCoupon || !couponCode.trim() || !selectedPlan}
              >
                {validatingCoupon ? t('offers.validating') : t('offers.apply')}
              </button>
            </div>
            {couponValidation && (
              <p className={`coupon-message ${couponValidation.success ? 'success' : 'error'}`}>
                {couponValidation.message}
              </p>
            )}
          </div>

          {/* Early bird runners already hold a code, and coupons can't be combined. */}
          {!earlyBird && (
            <div className="promo-section">
              <button className="btn-redeem-link" onClick={() => navigate('/redeem')}>
                {t('offers.haveCodeRedeem')}
              </button>
            </div>
          )}

          <button
            className="btn-continue"
            onClick={handleContinue}
            disabled={!selectedPlan}
          >
            {t('offers.continueToPayment')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Offers;
